// L'espace Gouvernance — decision cards sorted by waiting age, the whole review
// done from the queue (ux-design §4). Then the two inventory cuts and the
// official inventory artefact.
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Printer } from 'lucide-react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import { useT } from '../i18n';
import { formatDate } from '../utils/format';
import { BpmnCanvas } from '../components/BpmnCanvas';

type Tab = 'pending' | 'function' | 'owner' | 'inventory';
const GOV_CHECKS = ['roles', 'kpis', 'dashboards', 'inventory'];

export function GovernancePage() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>('pending');

  return (
    <>
      <div className="page-header">
        <div><h1 className="page-title">{t('shell.title.governance')}</h1></div>
      </div>
      <div className="tabs mb-16">
        {([['pending', t('gov.tab.pending')], ['function', t('gov.tab.byFunction')], ['owner', t('gov.tab.byOwner')], ['inventory', t('gov.tab.inventory')]] as [Tab, string][]).map(([k, label]) => (
          <button key={k} className={`tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      {tab === 'pending' && <PendingQueue onSeeInventory={() => setTab('inventory')} />}
      {tab === 'function' && <ByFunction />}
      {tab === 'owner' && <ByOwner />}
      {tab === 'inventory' && <Inventory />}
    </>
  );
}

function PendingQueue({ onSeeInventory }: { onSeeInventory: () => void }) {
  const { t } = useT();
  const { isGovernance, isAdmin } = useRole();
  const mayAct = isGovernance || isAdmin;
  const [rows, setRows] = useState<any[] | null>(null);
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [sendBack, setSendBack] = useState<number | null>(null);
  const [motif, setMotif] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const load = () => api.getGovernancePending().then(setRows).catch(() => setError(true));
  useEffect(() => { load(); api.getInventory().then(r => setInventoryCount(r.length)).catch(() => {}); }, []);
  useEffect(() => {
    if (openId == null) { setDetail(null); return; }
    api.getProcess(openId).then(setDetail).catch(() => {});
    api.getRoles(openId).then(setRoles).catch(() => setRoles([]));
  }, [openId]);

  if (error) return <div className="empty-state"><div className="empty-state-title">{t('gov.queueError')}</div><button className="btn btn-sm mt-16" onClick={() => { setError(false); load(); }}>{t('common.retry')}</button></div>;
  if (!rows) return <div className="page-loading"><div className="spinner" /></div>;

  if (rows.length === 0) return (
    <div className="empty-state">
      <div className="empty-state-title">{t('gov.emptyQueue')}</div>
      <div className="empty-state-desc">
        <button className="btn btn-ghost btn-sm" onClick={onSeeInventory}>{t('gov.emptyQueueCta', { n: inventoryCount })}</button>
      </div>
    </div>
  );

  const avg = Math.round(rows.reduce((s, r) => s + r.waiting_days, 0) / rows.length);
  const over = rows.filter(r => r.waiting_days > 14).length;

  const toggleCheck = async (row: any, key: string, checked: boolean) => {
    await api.setCheck(row.id, { phase_key: 'validation_governance', check_key: key, checked });
    load();
    if (openId === row.id) api.getProcess(row.id).then(setDetail);
  };

  const validate = async (row: any) => {
    try { await api.movePhase(row.id, 'final_validation'); setOpenId(null); load(); }
    catch (e: any) { setActionError(e.message); }
  };

  const doSendBack = async (row: any) => {
    try { await api.movePhase(row.id, 'kpi_definition', motif.trim()); setSendBack(null); setMotif(''); setOpenId(null); load(); }
    catch (e: any) { setActionError(e.message); }
  };

  return (
    <>
      {actionError && <div className="text-err text-sm mb-8">{actionError}</div>}
      <p className="page-meta mb-16">
        {t('gov.headline', { n: rows.length, avg })}
        {over > 0 && <> · <span className="text-warn">{t('gov.headlineOver', { n: over })}</span></>}
      </p>
      <div className="flex flex-col gap-8">
        {rows.map(row => {
          const checks = new Map((row.checks as any[]).map(c => [c.check_key, c.checked]));
          const missing = GOV_CHECKS.filter(k => !checks.get(k));
          const isOpen = openId === row.id;
          return (
            <div key={row.id} className="card">
              <div className="p-16">
                <div className="flex justify-between items-center">
                  <div>
                    <a className="font-medium text-ink-1" href={`#/process/${row.id}`}>{row.process_code} · {row.name}</a>
                    <div className="text-xs text-ink-3">
                      {row.process_owner_name && <>{t('rep.col.owner')} {row.process_owner_name} · </>}
                      {(row.functions as string[]).join(', ')}
                      {row.consultant_name && <> · {t('fiche.p1.consultant')} {row.consultant_name}</>}
                    </div>
                  </div>
                  <span className={`text-sm ${row.waiting_days > 14 ? 'text-warn' : 'text-ink-3'}`}>
                    {t('gov.waitingSince', { n: row.waiting_days })}{row.waiting_days > 14 ? ' ⚠' : ''}
                  </span>
                </div>

                {/* what blocks, readable without opening anything */}
                <div className="gov-checkline">
                  {GOV_CHECKS.map(k => (
                    <span key={k} className={checks.get(k) ? 'ok' : 'pending'}>
                      {mayAct
                        ? <input type="checkbox" checked={!!checks.get(k)} onChange={e => toggleCheck(row, k, e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                        : (checks.get(k) ? '✓' : '○')}{' '}
                      {t(`gov.check.${k}`)}
                    </span>
                  ))}
                </div>

                <div className="flex gap-8 items-center">
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(isOpen ? null : row.id)}>
                    {isOpen ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />} {t('gov.examine')}
                  </button>
                  <span style={{ marginLeft: 'auto' }} />
                  {mayAct && (
                    <>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--err)' }} onClick={() => setSendBack(row.id)}>
                        {t('gov.sendBack')}
                      </button>
                      {missing.length > 0
                        ? <span className="text-sm text-ink-3">{t('gov.validateBlocked', { missing: missing.map(k => t(`gov.check.${k}`)).join(', ') })}</span>
                        : <button className="btn btn-accent btn-sm" onClick={() => validate(row)}>{t('gov.validate')}</button>}
                    </>
                  )}
                </div>

                {sendBack === row.id && (
                  <div className="mt-12">
                    <div className="form-group">
                      <label className="form-label">{t('gov.sendBackMotif')}</label>
                      <textarea className="form-textarea" rows={2} autoFocus value={motif} onChange={e => setMotif(e.target.value)} />
                    </div>
                    <div className="flex gap-8 justify-end mt-8">
                      <button className="btn btn-ghost btn-sm" onClick={() => { setSendBack(null); setMotif(''); }}>{t('common.cancel')}</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--err)' }} disabled={!motif.trim()} onClick={() => doSendBack(row)}>
                        {t('gov.sendBack')}
                      </button>
                    </div>
                  </div>
                )}

                {isOpen && detail?.id === row.id && (
                  <div className="mt-12" style={{ borderTop: '1px solid var(--line)', paddingTop: 'var(--s12)' }}>
                    {roles.length > 0 && (
                      <>
                        <div className="detail-label">{t('fiche.rolesInDiagram')}</div>
                        <table className="roles-table mb-12">
                          <tbody>
                            {roles.filter(r => !r.stale).map(r => (
                              <tr key={r.id}>
                                <td>{r.lane_name}</td>
                                <td>{r.person_name ?? <span className="text-ink-3">{t('fiche.rolesUnassigned')}</span>}</td>
                                <td>{r.capacity_pct != null ? `${r.capacity_pct} %` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    <div className="detail-label">{t('fiche.p4.kpis')}</div>
                    {detail.kpis.length === 0 ? <div className="text-sm text-ink-3 mb-8">{t('fiche.p4.noKpis')}</div> :
                      detail.kpis.map((k: any) => (
                        <div key={k.id} className="detail-row"><span className="detail-row-label" style={{ minWidth: 140 }}>{k.name}</span><span className="text-sm">{k.definition}</span></div>
                      ))}
                    {detail.git_path && (
                      <div className="mt-12" style={{ height: 260, border: '1px solid var(--line)', position: 'relative' }}>
                        <GovDiagramThumb path={detail.git_path} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function GovDiagramThumb({ path }: { path: string }) {
  const [xml, setXml] = useState<string | null>(null);
  useEffect(() => { api.getRepoFile(path).then(f => setXml(f.content)).catch(() => {}); }, [path]);
  return <BpmnCanvas xml={xml} fill />;
}

function ByFunction() {
  const { t, lang } = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  const [openUndeclared, setOpenUndeclared] = useState<Set<number>>(new Set());

  useEffect(() => { api.getByFunction().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <div className="page-loading"><div className="spinner" /></div>;
  if (rows.length === 0) return <div className="empty-state"><div className="empty-state-desc">{t('gov.fn.empty')}</div></div>;

  const sorted = [...rows].sort((a, b) => (b.processes?.length ?? 0) - (a.processes?.length ?? 0));

  return (
    <div className="flex flex-col gap-16">
      {sorted.map(fn => {
        const procs = fn.processes as any[];
        const live = procs.filter(p => p.in_inventory).length;
        const isOpen = openUndeclared.has(fn.id);
        return (
          <div key={fn.id} className="card">
            <div className="card-header">
              <h3 className="card-title">{fn.name}</h3>
              <span className="text-sm text-ink-3">{t('gov.fn.count', { n: procs.length, live, cycle: procs.length - live })}</span>
            </div>
            <div className="p-16">
              {procs.map(p => (
                <div key={p.id} className="detail-row list-row-clickable" style={{ padding: 'var(--s4) 0' }}
                     onClick={() => { window.location.hash = `#/process/${p.id}`; }}>
                  <span className="detail-row-label mono" style={{ minWidth: 60 }}>{p.process_code ?? '—'}</span>
                  <span className="flex-1">{p.name}</span>
                  <span className="state-chip state-chip--cycle">{t('rep.state.step', { n: p.phase_order })} · {lang === 'fr' ? p.phase_label_fr : p.phase_label_en}</span>
                  {p.process_owner_name && <span className="text-sm text-ink-3">{p.process_owner_name}</span>}
                </div>
              ))}
              {fn.undeclared_lanes?.length > 0 && (
                <div style={{ borderTop: '1px solid var(--line)', marginTop: 'var(--s8)', paddingTop: 'var(--s8)' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpenUndeclared(s => {
                    const next = new Set(s); next.has(fn.id) ? next.delete(fn.id) : next.add(fn.id); return next;
                  })}>
                    {isOpen ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />}
                    {t('gov.fn.undeclared', { n: fn.undeclared_lanes.length })}
                  </button>
                  {isOpen && fn.undeclared_lanes.map((u: any) => (
                    <div key={u.path} className="detail-row" style={{ paddingLeft: 'var(--s16)' }}>
                      <span className="flex-1 text-sm">{u.title} <span className="rep-path">{u.path}</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ByOwner() {
  const { t, lang } = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => { api.getByOwner().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <div className="page-loading"><div className="spinner" /></div>;
  if (rows.length === 0) return <div className="empty-state"><div className="empty-state-desc">—</div></div>;

  return (
    <div className="flex flex-col gap-16">
      {rows.map(o => (
        <div key={o.id} className="card">
          <div className="card-header">
            <h3 className="card-title">{o.name}{o.function_name && <span className="badge" style={{ marginLeft: 8 }}>{o.function_name}</span>}</h3>
            <span className="text-sm text-ink-3">
              {t('gov.fn.count', { n: o.processes.length, live: o.live_count, cycle: o.processes.length - o.live_count })}
              {o.overloaded && <span className="badge badge-warn" style={{ marginLeft: 8 }}>{t('gov.owner.overloaded')} ⚠</span>}
            </span>
          </div>
          <div className="p-16">
            {o.capacity_total != null ? (
              <div className="capacity-bar mb-12">
                <span>{t('gov.owner.load', { pct: o.capacity_total })}</span>
                <span className="capacity-track">
                  <span className={`capacity-fill${o.capacity_total > 100 ? ' capacity-fill--over' : ''}`}
                        style={{ width: `${Math.min(o.capacity_total, 100)}%`, display: 'block' }} />
                </span>
              </div>
            ) : <div className="text-sm text-ink-3 mb-12" style={{ fontStyle: 'italic' }}>{t('gov.owner.noCapacity')}</div>}
            {(o.processes as any[]).map(p => (
              <div key={p.id} className="detail-row list-row-clickable" style={{ padding: 'var(--s4) 0' }}
                   onClick={() => { window.location.hash = `#/process/${p.id}`; }}>
                <span className="detail-row-label mono" style={{ minWidth: 60 }}>{p.process_code}</span>
                <span className="flex-1">{p.name}</span>
                <span className="state-chip state-chip--cycle">{t('rep.state.step', { n: p.phase_order })} · {lang === 'fr' ? p.phase_label_fr : p.phase_label_en}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Inventory() {
  const { t, lang } = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => { api.getInventory().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <div className="page-loading"><div className="spinner" /></div>;
  if (rows.length === 0) return <div className="empty-state"><div className="empty-state-desc">{t('gov.inv.empty')}</div></div>;

  return (
    <>
      <div className="flex justify-between items-center mb-16">
        <p className="page-meta">{t('rep.count', { n: rows.length })}</p>
        <button className="btn btn-export" onClick={() => window.print()}><Printer className="icon-sm" /> {t('gov.inv.print')}</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 70 }}>{t('rep.colCode')}</th><th>{t('rep.col.process')}</th>
            <th>{t('rep.col.owner')}</th><th>{t('rep.col.functions')}</th><th style={{ width: 120 }}>{t('gov.inv.since')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="list-row-clickable" onClick={() => { window.location.hash = `#/process/${r.id}`; }}>
              <td className="mono text-sm">{r.process_code}</td>
              <td><span className="font-medium text-ink-1">{r.name}</span><div className="rep-path">{r.git_path ?? ''}</div></td>
              <td className="text-sm">{r.process_owner_name ?? '—'}</td>
              <td>{(r.functions as string[]).map(f => <span key={f} className="badge">{f}</span>)}</td>
              <td className="text-sm text-ink-3">{formatDate(r.inventory_added_at, lang)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
